import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import CurrencyForm from "./CurrencyForm";
import CurrencyList from "./CurrencyList";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import BackButton from "../../../../components/Button/BackButton";
import { toast } from "../../../../components/Toast/toast";

const CurrencyPage = () => {
  const [currencies, setCurrencies] = useState([]);
  const [userCurrencies, setUserCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currencyToDelete, setCurrencyToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  useEffect(() => {
    fetchCurrencies();
    fetchUserCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const res = await api.get("/currencies");
      setCurrencies(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUserCurrencies = async () => {
    try {
      const res = await api.get("/admin-currencies");
      setUserCurrencies(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch currencies");
    } finally {
      setLoading(false);
    }
  };

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (formData) => {
    try {
      if (action === "edit" && editId) {
        await api.put(`/admin-currencies/${editId}`, formData);
      } else {
        await api.post("/admin-currencies", formData);
      }
      setAction(null);
      fetchUserCurrencies();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save currency");
    }
  };

  const handleDelete = async () => {
    if (!currencyToDelete) return;
    try {
      await api.delete(`/admin-currencies/${currencyToDelete}`);
      setCurrencyToDelete(null);
      fetchUserCurrencies();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete currency");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const inForm = action === "add" || (action === "edit" && editId);
  const editingCurrency =
    action === "edit" && editId
      ? userCurrencies.find((c) => String(c.id) === String(editId))
      : null;

  return (
    <>
      <div className="md:w-1/2">
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton
              onClick={inForm ? () => setAction(null) : undefined}
            />
            <h1 className="text-2xl font-bold text-gray-900">Currencies</h1>
          </div>
          {!inForm && (
            <button
              onClick={() => setAction("add")}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
            >
              Add Currency
            </button>
          )}
        </div>

        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

        {inForm ? (
          <CurrencyForm
            currencyToEdit={editingCurrency}
            currencies={currencies}
            onSave={handleSave}
            onCancel={() => setAction(null)}
          />
        ) : (
          <CurrencyList
            userCurrencies={userCurrencies}
            onEdit={(c) => setAction("edit", c.id)}
            onDelete={(id) => setCurrencyToDelete(id)}
          />
        )}
      </div>

      {currencyToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this currency? This action cannot be undone."
          onCancel={() => setCurrencyToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
          confirmColor="bg-red-600"
          confirmHoverColor="hover:bg-red-700"
        />
      )}
    </>
  );
};

export default CurrencyPage;
